import { ReactNode, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../store/authStore'
import { isSupportedLocale, DEFAULT_LOCALE } from './locales'

interface Props {
  children: ReactNode
}

export default function LanguageProvider({ children }: Props) {
  const { i18n } = useTranslation()
  const userLanguage = useAuthStore((s) => s.user?.language)

  // Sync html[lang] + Sentry language tag whenever i18next's active language changes
  // (covers both the authenticated-user path and the anonymous LanguageSelector path).
  useEffect(() => {
    const sync = (lng: string) => {
      document.documentElement.lang = lng
      Sentry.setTag('language', lng)
    }
    sync(i18n.language)
    i18n.on('languageChanged', sync)
    return () => {
      i18n.off('languageChanged', sync)
    }
  }, [i18n])

  // When an authenticated user's language preference is known, apply it.
  // If userLanguage is undefined (not logged in), do nothing — i18next's own
  // detector already resolved the locale from localStorage / navigator on init,
  // and we must not override it with DEFAULT_LOCALE just because no user is present.
  useEffect(() => {
    if (userLanguage === undefined) return
    const locale = isSupportedLocale(userLanguage) ? userLanguage : DEFAULT_LOCALE
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale)
    }
  }, [i18n, userLanguage])

  return <>{children}</>
}
